import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { computePivot, defaultConfig, sortPivotRows } from "./engine";
import { applyShowAs } from "./showAs";
import type {
  PivotConfig,
  PivotField,
  PivotResult,
  PivotRowNode,
  PivotSort,
} from "./types";

export const CLIENT_ROW_LIMIT = 50_000;
const SEARCH_DEBOUNCE_MS = 300;

export interface PivotFetchRequest {
  config: PivotConfig;
  /** Set when loading the children of one row */
  parentPath?: string[];
  /** The table's search text, so server pivots match the rows the table shows */
  search?: string;
  /** Aborted when the config or search changes, or the pivot unmounts */
  signal?: AbortSignal;
}

export interface UsePivotControllerOptions<TData> {
  fields: PivotField<TData>[];
  initialConfig: PivotConfig;
  /** Full dataset. Pivoted in the browser when it has at most `clientRowLimit` rows */
  data?: TData[];
  /** Django pivot endpoint. Used when there's no `data` or it's over the limit */
  fetchPivot?: (request: PivotFetchRequest) => Promise<PivotResult>;
  clientRowLimit?: number;
  initiallyExpanded?: "none" | "all";
  /** Saves the config, and whether every row is expanded, for the browser tab. Row values aren't saved because they can contain PHI */
  stateKey?: string;
  /** Skips pivoting and fetching while the pivot isn't shown */
  enabled?: boolean;
  /** Sent with server requests; client pivots get rows that are already filtered */
  search?: string;
}

export interface PivotController<TData> {
  fields: PivotField<TData>[];
  config: PivotConfig;
  setConfig: (
    updater: PivotConfig | ((config: PivotConfig) => PivotConfig),
  ) => void;
  setSort: (sort: PivotSort | undefined) => void;
  result: PivotResult;
  mode: "client" | "server";
  isLoading: boolean;
  error: string | null;
  retry: () => void;
  /** Set when loading one row's children failed; that row's siblings stay visible */
  rowError: (key: string) => string | undefined;
  retryRow: (row: PivotRowNode) => void;
  isExpanded: (key: string) => boolean;
  hasExpanded: () => boolean;
  expandedState: true | Record<string, boolean>;
  toggleExpanded: (row: PivotRowNode) => void;
  expandAll: () => void;
  collapseAll: () => void;
}

const EMPTY_RESULT: PivotResult = {
  rows: [],
  columnKeys: [],
  grandTotal: {},
  recordCount: 0,
};

const STORAGE_PREFIX = "pivotV2:";

interface Persisted {
  config?: PivotConfig;
  expanded?: "all";
}

const readPersisted = (stateKey?: string): Persisted => {
  if (!stateKey) return {};
  try {
    return JSON.parse(
      window.sessionStorage.getItem(STORAGE_PREFIX + stateKey) ?? "{}",
    );
  } catch {
    return {};
  }
};

const groupKeys = (rows: PivotRowNode[], keys: string[] = []) => {
  rows.forEach((row) => {
    if (row.hasChildren) keys.push(row.key);
    if (row.children) groupKeys(row.children, keys);
  });
  return keys;
};

const useDebouncedValue = <T>(value: T, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
};

const insertChildren = (
  rows: PivotRowNode[],
  key: string,
  children: PivotRowNode[],
): PivotRowNode[] =>
  rows.map((row) => {
    if (row.key === key) return { ...row, children };
    return row.children
      ? { ...row, children: insertChildren(row.children, key, children) }
      : row;
  });

const findRow = (
  rows: PivotRowNode[],
  key: string,
): PivotRowNode | undefined => {
  for (const row of rows) {
    if (row.key === key) return row;
    const found = row.children && findRow(row.children, key);
    if (found) return found;
  }
  return undefined;
};

export const usePivotController = <TData>({
  fields,
  initialConfig,
  data,
  fetchPivot,
  clientRowLimit = CLIENT_ROW_LIMIT,
  initiallyExpanded = "none",
  stateKey,
  enabled = true,
  search,
}: UsePivotControllerOptions<TData>): PivotController<TData> => {
  const [persisted] = useState(() => readPersisted(stateKey));
  const [config, setConfigState] = useState<PivotConfig>(
    () => persisted.config ?? initialConfig,
  );
  const [expanded, setExpanded] = useState<Set<string> | "all">(() =>
    persisted.expanded === "all" || initiallyExpanded === "all"
      ? "all"
      : new Set(),
  );
  const [serverResult, setServerResult] = useState<PivotResult>(EMPTY_RESULT);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const mode: "client" | "server" =
    data && (data.length <= clientRowLimit || !fetchPivot)
      ? "client"
      : fetchPivot
        ? "server"
        : "client";

  const { sort, ...queryConfig } = config;
  const queryKey = JSON.stringify([queryConfig, debouncedSearch]);
  // Bumped per root load so child responses for an older config or search are dropped
  const generation = useRef(0);
  const rootAbort = useRef<AbortController | null>(null);
  const loadingChildren = useRef(new Set<string>());

  const clientResult = useMemo(
    () =>
      enabled && mode === "client" && data
        ? computePivot({ data, fields, config: queryConfig })
        : EMPTY_RESULT,
    // queryKey stands in for the config without sort, which is applied after
    [enabled, mode, data, fields, JSON.stringify(queryConfig)],
  );

  useEffect(() => {
    if (!enabled || mode !== "server" || !fetchPivot) return;
    let cancelled = false;
    const abort = new AbortController();
    rootAbort.current = abort;
    generation.current += 1;
    loadingChildren.current.clear();
    setIsLoading(true);
    setError(null);
    setRowErrors({});
    fetchPivot({
      config: queryConfig,
      search: debouncedSearch,
      signal: abort.signal,
    })
      .then((result) => !cancelled && setServerResult(result))
      .catch(
        () => !cancelled && setError("Something went wrong loading this pivot"),
      )
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [enabled, mode, queryKey, attempt]);

  const raw = mode === "client" ? clientResult : serverResult;
  const result = useMemo(() => {
    const transformed = applyShowAs(raw, defaultConfig(config));
    return { ...transformed, rows: sortPivotRows(transformed.rows, sort) };
  }, [raw, config, sort]);

  useEffect(() => {
    if (!stateKey) return;
    try {
      window.sessionStorage.setItem(
        STORAGE_PREFIX + stateKey,
        JSON.stringify({
          config,
          expanded: expanded === "all" ? "all" : undefined,
        } satisfies Persisted),
      );
    } catch {
      // Storage can be unavailable - the pivot still works without it
    }
  }, [stateKey, config, expanded]);

  const setConfig = useCallback<PivotController<TData>["setConfig"]>(
    (updater) => {
      setConfigState((current) => {
        const next = typeof updater === "function" ? updater(current) : updater;
        const shapeChanged =
          JSON.stringify([next.rows, next.columns]) !==
          JSON.stringify([current.rows, current.columns]);
        if (shapeChanged) setExpanded(new Set());
        return next;
      });
    },
    [],
  );

  const isExpanded = useCallback(
    (key: string) => (expanded === "all" ? true : expanded.has(key)),
    [expanded],
  );

  const loadChildren = useCallback(
    (row: PivotRowNode) => {
      if (mode !== "server" || !fetchPivot || row.isOther) return;
      if (findRow(serverResult.rows, row.key)?.children) return;
      if (loadingChildren.current.has(row.key)) return;
      loadingChildren.current.add(row.key);
      const requestGeneration = generation.current;
      fetchPivot({
        config: queryConfig,
        parentPath: row.path,
        search: debouncedSearch,
        signal: rootAbort.current?.signal,
      })
        .then((childResult) => {
          if (requestGeneration !== generation.current) return;
          setServerResult((current) => {
            return {
              ...current,
              rows: insertChildren(current.rows, row.key, childResult.rows),
            };
          });
        })
        .catch(() => {
          if (requestGeneration === generation.current) {
            setRowErrors((current) => {
              return {
                ...current,
                [row.key]: "Something went wrong loading these rows",
              };
            });
          }
        })
        .finally(() => loadingChildren.current.delete(row.key));
    },
    [mode, fetchPivot, serverResult, queryKey],
  );

  const toggleExpanded = useCallback(
    (row: PivotRowNode) => {
      const open = isExpanded(row.key);
      setExpanded((current) => {
        const next = new Set(current === "all" ? groupKeys(raw.rows) : current);
        if (open) next.delete(row.key);
        else next.add(row.key);
        return next;
      });
      if (!open) loadChildren(row);
    },
    [isExpanded, raw, loadChildren],
  );

  // Expand all on a server pivot loads each level's children as the level above arrives
  useEffect(() => {
    if (expanded !== "all" || mode !== "server") return;
    const visit = (rows: PivotRowNode[]) =>
      rows.forEach((row) => {
        if (row.children) visit(row.children);
        else if (row.hasChildren && !rowErrors[row.key]) loadChildren(row);
      });
    visit(serverResult.rows);
  }, [expanded, mode, serverResult, loadChildren, rowErrors]);

  const expandedState = useMemo(
    () =>
      expanded === "all"
        ? (true as const)
        : Object.fromEntries(Array.from(expanded, (key) => [key, true])),
    [expanded],
  );

  return {
    fields,
    config,
    setConfig,
    setSort: (nextSort) =>
      setConfig((current) => {
        return { ...current, sort: nextSort };
      }),
    result,
    mode,
    isLoading,
    error,
    retry: () => setAttempt((n) => n + 1),
    rowError: (key) => rowErrors[key],
    retryRow: (row) => {
      setRowErrors(({ [row.key]: _failed, ...rest }) => rest);
      loadChildren(row);
    },
    isExpanded,
    hasExpanded: () => expanded === "all" || expanded.size > 0,
    expandedState,
    toggleExpanded,
    expandAll: () => setExpanded("all"),
    collapseAll: () => setExpanded(new Set()),
  };
};
